import { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useFormik } from "formik";
import Drawer from "../../../../../components/SideDrawer/SideDrawer";
import { CreateViewForm } from "../../HelpDesk/components/CreateView/components/CreateViewForm";
import { CrmCreateViewFilters } from "./CrmCreateViewFilters";
import {
  type ViewFormValues,
  INITIAL_VIEW_FORM_VALUES,
  viewFormValidationSchema,
  CRM_ATTRIBUTION_FIELD_OPTIONS,
} from "../utlis/crmViewConstants";
import {
  getSegmentExtraFilterFields,
  SEGMENT_EXTRA_OPERATORS,
  SEGMENT_EXTRA_VALUE_OPTIONS,
  SEGMENT_EXTRA_SINGLE_VALUE_FIELDS,
  SEGMENT_FREE_TEXT_FIELDS,
} from "../../../../Campaigns/components/Segments/constants/segments.constants";
import {
  mapFilterGroupsToApi,
  apiFilterGroupsToUI,
} from "../../../../Campaigns/components/Segments/utils/segments.utils";
import { customToast } from "../../../../../utils/custom-toast/custom-toast";
import {
  useCreateCrmViewMutation,
  useUpdateCrmViewMutation,
} from "../../../../../redux/services/crmApi";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../../../redux/store";
import {
  setNewlyCreatedViewId,
  addCrmView,
  updateCrmView,
} from "../../../../../redux/slices/crmLeadListSlice";
import { clearDrawer } from "../../../../../redux/slices/drawerSlice";
import { trackEvents } from "../../../../../utils/amplitude/amplitude";
import { getClientDomain } from "../../../../../utils/functions/client-code";
import { CRM_EVENTS } from "../../../utils/crmTracking.constants";

export const CrmCreateEditView = () => {
  const dispatch = useDispatch();
  const drawerState = useSelector((state: RootState) => state.drawer);
  const crmViews = useSelector(
    (state: RootState) => state.crmLeadList.crmViews
  );
  const clientRegion = useSelector(
    (state: RootState) => state.auth?.client?.region
  );
  const segmentExtraFilterFields = useMemo(
    () => [
      ...getSegmentExtraFilterFields(clientRegion),
      ...CRM_ATTRIBUTION_FIELD_OPTIONS,
    ],
    [clientRegion]
  );
  const isDrawerOpen = Boolean(
    drawerState?.open &&
      (drawerState?.type === "crm_view" ||
        drawerState?.type === "crm_edit_view")
  );
  const isEditMode = drawerState?.type === "crm_edit_view";
  const editViewId = isEditMode ? drawerState?.id : undefined;

  const [step, setStep] = useState<"form" | "filters">("form");
  const [viewEmoji, setViewEmoji] = useState("");

  const [createCrmView] = useCreateCrmViewMutation();
  const [updateCrmViewApi] = useUpdateCrmViewMutation();

  const editViewData = useMemo(
    () => crmViews.find((v) => v.id === editViewId),
    [crmViews, editViewId]
  );

  const formik = useFormik<ViewFormValues>({
    initialValues: INITIAL_VIEW_FORM_VALUES,
    validationSchema: viewFormValidationSchema,
    onSubmit: async (values) => {
      const filterGroups = mapFilterGroupsToApi(values?.filter_groups);

      const payload = {
        name: values?.name,
        scope: values?.scope as "private" | "public",
        filter_groups: filterGroups,
        logical_operator: values?.logical_operator,
        emoji_icon: viewEmoji,
      };

      try {
        if (editViewId) {
          const result = await updateCrmViewApi({
            viewId: editViewId,
            ...payload,
          }).unwrap();
          dispatch(
            updateCrmView({
              id: editViewId,
              ...payload,
              updated_at: result?.updated_at,
            })
          );
          trackEvents(CRM_EVENTS.VIEW_UPDATED, {
            view_id: editViewId,
            view_name: payload.name,
            module: "crm",
            org_id: getClientDomain(),
          });
          customToast("View updated successfully", "success");
        } else {
          const result = await createCrmView(payload).unwrap();
          const newView = {
            id: result?.id ?? `crm_view_${Date.now()}`,
            ...payload,
            created_at: result?.created_at,
            updated_at: result?.updated_at,
          };
          dispatch(addCrmView(newView));
          dispatch(setNewlyCreatedViewId(newView.id));
          trackEvents(CRM_EVENTS.VIEW_CREATED, {
            view_id: newView.id,
            view_name: payload.name,
            module: "crm",
            org_id: getClientDomain(),
          });
          customToast("View created successfully", "success");
        }
        handleClose();
      } catch (error: unknown) {
        const errorMessage =
          (error as { data: { detail: string } })?.data?.detail ||
          "Failed to save view";
        customToast(errorMessage, "error");
      }
    },
  });

  const handleClose = () => {
    setStep("form");
    setViewEmoji("");
    formik.resetForm({ values: INITIAL_VIEW_FORM_VALUES });
    dispatch(clearDrawer());
  };

  const handleNext = async () => {
    formik.setFieldTouched("name", true);
    formik.setFieldTouched("scope", true);
    const errors = await formik.validateForm();
    if (errors.name || errors.scope) return;
    setStep("filters");
  };

  useEffect(() => {
    if (!isDrawerOpen || drawerState?.type !== "crm_view") return;
    setStep("form");
    setViewEmoji("");
    formik.resetForm({ values: INITIAL_VIEW_FORM_VALUES });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, drawerState?.type]);

  useEffect(() => {
    if (!isEditMode || !editViewData) return;
    formik.resetForm({
      values: {
        name: editViewData.name || "",
        scope: (editViewData.scope as "private" | "public") || "",
        filter_groups: apiFilterGroupsToUI(
          editViewData.filter_groups as Parameters<typeof apiFilterGroupsToUI>[0]
        ),
        logical_operator:
          (editViewData.logical_operator as "and" | "or") || "and",
      },
    });
    setViewEmoji(editViewData.emoji_icon || "");
    setStep("filters");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editViewData]);

  const hasUnsavedChanges = useMemo(() => {
    const currentValues = formik.values;
    const initialValues = formik.initialValues;
    return (
      !_.isEqual(currentValues.filter_groups, initialValues.filter_groups) ||
      currentValues.logical_operator !== initialValues.logical_operator
    );
  }, [formik.values, formik.initialValues]);

  const scopeDisplay =
    formik.values.scope === "public"
      ? "everyone"
      : formik.values.scope === "private"
        ? "private"
        : "";

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={handleClose}
      direction="right"
      size="550px"
      overlayColor="rgba(0,0,0,0.7)"
      duration={300}
      closeOnOverlayClick={true}
      closeOnEscape={true}
      drawerContainerClassName="rounded-l-[8px]"
    >
      {step === "form" ? (
        <CreateViewForm
          formTitle={isEditMode ? "Edit view" : "Create view"}
          viewName={formik.values.name}
          viewEmoji={viewEmoji}
          sharedWith={scopeDisplay}
          onViewNameChange={(name) => formik.setFieldValue("name", name)}
          onEmojiChange={setViewEmoji}
          onSharedWithChange={(value) =>
            formik.setFieldValue(
              "scope",
              value === "everyone" ? "public" : "private"
            )
          }
          scopeError={
            formik.touched.scope && formik.errors.scope
              ? formik.errors.scope
              : ""
          }
          onClose={handleClose}
          onNext={handleNext}
          isEditMode={isEditMode}
        />
      ) : (
        <CrmCreateViewFilters
          filterGroups={formik.values.filter_groups}
          logicalOperator={formik.values.logical_operator}
          onFilterGroupsChange={(groups) =>
            formik.setFieldValue("filter_groups", groups)
          }
          onLogicalOperatorChange={(operator) =>
            formik.setFieldValue("logical_operator", operator)
          }
          onBack={() => setStep("form")}
          onClose={handleClose}
          onSave={() => formik.handleSubmit()}
          isEditMode={isEditMode}
          isSaveViewLoading={formik.isSubmitting}
          hasUnsavedChanges={hasUnsavedChanges}
          extraFieldOptions={segmentExtraFilterFields}
          extraOperators={SEGMENT_EXTRA_OPERATORS}
          extraValueOptions={SEGMENT_EXTRA_VALUE_OPTIONS}
          extraSingleValueFields={SEGMENT_EXTRA_SINGLE_VALUE_FIELDS}
          extraFreeTextFields={SEGMENT_FREE_TEXT_FIELDS}
          showSectionedPicker
        />
      )}
    </Drawer>
  );
};
